import type { SearchQueryBody } from "@/types";

/**
 * TanStack-Query key factory for Session reads. The hierarchy
 * `all` ⊃ `lists()` ⊃ `list(orgId, query)` for list reads, and
 * `all` ⊃ `detail(sessionId)` ⊃ `topics(sessionId, pageToken)` /
 * `files(sessionId, pageToken, limit)` / `devices(sessionId)` for
 * per-session reads, lets mutations invalidate at the broadest level they
 * affect.
 */
export const sessionQueryKeys = {
  all: ["sessions"] as const,
  lists: () => [...sessionQueryKeys.all, "list"] as const,
  // Collapse `undefined` and `{}` to the orgId-only shape so callers
  // that build a body conditionally share a cache slot with callers
  // that pass nothing. `{}` is truthy in JS, so a `query`-truthiness
  // check would mint a distinct 4-element key for it.
  list: (orgId: string | undefined, query?: SearchQueryBody) =>
    query !== undefined && Object.keys(query).length > 0
      ? ([...sessionQueryKeys.lists(), orgId, query] as const)
      : ([...sessionQueryKeys.lists(), orgId] as const),
  detail: (sessionId: string) =>
    [...sessionQueryKeys.all, "detail", sessionId] as const,
  // Omit `pageToken` from the key tail when it's undefined so the first
  // page shares a cache slot regardless of whether the caller passed it.
  topics: (sessionId: string, pageToken?: string) =>
    pageToken === undefined
      ? ([...sessionQueryKeys.detail(sessionId), "topics"] as const)
      : ([...sessionQueryKeys.detail(sessionId), "topics", pageToken] as const),
  devices: (sessionId: string) =>
    [...sessionQueryKeys.detail(sessionId), "devices"] as const,
  files: (sessionId: string, pageToken?: string, limit?: number) =>
    pageToken === undefined && limit === undefined
      ? ([...sessionQueryKeys.detail(sessionId), "files"] as const)
      : ([
          ...sessionQueryKeys.detail(sessionId),
          "files",
          { limit, pageToken },
        ] as const),
};
